import { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function Gallery({ items }) {
  const { t, pick } = useLang();
  const [active, setActive] = useState(null); // index

  useEffect(() => {
    const onKey = (e) => {
      if (active === null) return;
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") setActive((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft")
        setActive((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, items.length]);

  if (!items?.length) return null;

  return (
    <section id="gallery" className="afb-section bg-afb-bg" data-testid="gallery-section">
      <div className="afb-container">
        <div className="max-w-2xl mb-10 sm:mb-14">
          <span className="afb-eyebrow">{t("sections.gallery_label")}</span>
          <h2 className="serif text-[1.7rem] sm:text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.15] text-afb-ink">
            {t("sections.gallery_subtitle")}
          </h2>
          <span className="block mt-5 h-[2px] w-16 sm:w-20 bg-[color:var(--afb-gold)]" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {items.map((it, idx) => (
            <button
              key={it.id}
              onClick={() => setActive(idx)}
              className="group relative overflow-hidden rounded-md aspect-[4/3] bg-black"
              data-testid={`gallery-item-${it.id}`}
            >
              <img
                src={it.url}
                alt={pick(it, "caption") || "gallery"}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 via-black/30 to-transparent text-white opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-xs uppercase tracking-[0.22em]">
                  {pick(it, "caption")}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {active !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="lightbox"
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActive((i) => (i - 1 + items.length) % items.length);
            }}
            className="absolute left-4 sm:left-8 text-white/80 hover:text-white p-3"
            aria-label="prev"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>

          <img
            src={items[active].url}
            alt={pick(items[active], "caption")}
            className="max-w-full max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            onClick={(e) => {
              e.stopPropagation();
              setActive((i) => (i + 1) % items.length);
            }}
            className="absolute right-4 sm:right-8 text-white/80 hover:text-white p-3"
            aria-label="next"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-7 h-7" />
          </button>

          <button
            onClick={() => setActive(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-3"
            aria-label="close"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>

          {pick(items[active], "caption") && (
            <div className="absolute bottom-6 inset-x-0 text-center text-white/80 text-sm">
              {pick(items[active], "caption")}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
